import React, { FunctionComponent, memo, useRef } from "react";
import { Grid, Box, FormControlLabel, RadioGroup, Radio } from "@mui/material";
import { Input } from "@shared/components";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { FIELD_TITLES } from "@investorRequest/constants/fieldTooltips";
import InvestorRequestFormTitle from "@investorRequest/components/InvestorRequestFormTitle";
import { validationMessages } from "@investorRequest/constants/validationMessages";
import {
  sanitizeAsciiAlnum,
  sanitizeAsciiName,
  sanitizeDigits,
  sanitizePaymentMessage,
  isValidRoutingNumber,
  isValidPaymentMessage,
} from "@investorRequest/utils/SpecificValidations";
import { useC2DSErrors } from "@investorRequest/hooks/useC2DSFieldError";
import C2DSFieldErrorMessages from "@investorRequest/components/C2DSFieldErrorMessages";

interface Props {
  yesNoValue: string; // "yes" | "no" | ""
  swiftOrAba: string;
  bankName: string;
  routingNumber?: string;
  paymentMessage?: string;
  onChangeYesNo(value: string): void;
  onChangeSwiftOrAba(value: string): void;
  onChangeBankName(value: string): void;
  onChangeRoutingNumber?: (value: string) => void;
  onChangePaymentMessage?: (value: string) => void;
  showErrors: boolean;
}

const MAX_INTERMEDIARY_ID_LENGTH = 12;
const MAX_ROUTING_NUMBER = 9;
const MAX_PAYMENT_MESSAGE = 40;

// IR-UI-Revamp_V1: Restored Yes/No radio buttons for intermediary instructions
const IntermediaryFields: FunctionComponent<Props> = ({
  yesNoValue,
  swiftOrAba,
  bankName,
  routingNumber = "",
  paymentMessage = "",
  onChangeYesNo,
  onChangeSwiftOrAba,
  onChangeBankName,
  onChangeRoutingNumber,
  onChangePaymentMessage,
  showErrors,
}) => {
  const radioTopRef = useRef<HTMLDivElement | null>(null);
  const fieldsStartRef = useRef<HTMLDivElement | null>(null);

  // LPC-10289: C2DS validation errors
  const { hasFieldError, clearFieldError } = useC2DSErrors();

  // LPC-10289: Cache C2DS error states to ensure consistency between error prop and message display
  const hasIntermediarySwiftC2DSError = hasFieldError("intermediarySwiftOrAba");
  const hasIntermediaryRoutingC2DSError = hasFieldError("intermediaryRoutingNumber");

  // Intermediary ABA/SWIFT is required only when Yes is selected
  const showSwiftOrAbaError = showErrors && yesNoValue === "yes" && !swiftOrAba;

  return (
    <>
      <InvestorRequestFormTitle text="Do these wire instructions contain intermediary instructions? (Yes/No)" />
      <Grid item xs={12}>
        <div ref={radioTopRef} />
        <RadioGroup row value={yesNoValue || ""}>
          <FormControlLabel
            value="yes"
            control={<Radio size="small" />}
            label="Yes"
            onClick={() => onChangeYesNo("yes")}
          />
          <FormControlLabel
            value="no"
            control={<Radio size="small" />}
            label="No"
            onClick={() => onChangeYesNo("no")}
          />
        </RadioGroup>
      </Grid>
      {yesNoValue === "yes" && (
        <>
          <div ref={fieldsStartRef} />
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.IntermediaryAbaSwift}>
              <Input
                dense={false}
                placeholder="*Intermediary ABA / SWIFT"
                variant="light"
                size="large"
                value={swiftOrAba}
                onChange={(v) => {
                  // LPC-10289: Clear C2DS error when user modifies field
                  clearFieldError("intermediarySwiftOrAba");
                  onChangeSwiftOrAba(sanitizeAsciiAlnum(v, MAX_INTERMEDIARY_ID_LENGTH));
                }}
                sanitizeOnPaste={(t) => sanitizeAsciiAlnum(t, MAX_INTERMEDIARY_ID_LENGTH)}
                error={showSwiftOrAbaError || hasIntermediarySwiftC2DSError}
              />
            </FieldTooltip>
            {showSwiftOrAbaError && (
              <Box mt={1} color="warning.main" fontSize={12}>
                {validationMessages.intermediary.swiftOrAba.required}
              </Box>
            )}
            {showErrors && !!swiftOrAba && swiftOrAba.length > MAX_INTERMEDIARY_ID_LENGTH && (
              <Box mt={1} color="warning.main" fontSize={12}>
                Max 12 characters
              </Box>
            )}
            {/* LPC-10289: C2DS validation error */}
            <C2DSFieldErrorMessages fieldKey="intermediarySwiftOrAba" />
          </Grid>
          {/* Routing # field - shown for both SWIFT and FedWire */}
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.IntermediaryRoutingNumber}>
              <Input
                dense={false}
                placeholder="Routing #"
                variant="light"
                size="large"
                value={routingNumber}
                onChange={(v) => {
                  // LPC-10289: Clear C2DS error when user modifies field
                  clearFieldError("intermediaryRoutingNumber");
                  onChangeRoutingNumber?.(sanitizeDigits(v, MAX_ROUTING_NUMBER));
                }}
                sanitizeOnPaste={(t) => sanitizeDigits(t, MAX_ROUTING_NUMBER)}
                error={
                  (showErrors && !!routingNumber && !isValidRoutingNumber(routingNumber)) ||
                  hasIntermediaryRoutingC2DSError
                }
              />
            </FieldTooltip>
            {showErrors && !!routingNumber && routingNumber.length > MAX_ROUTING_NUMBER && (
              <Box mt={1} color="warning.main" fontSize={12}>
                Max 9 characters
              </Box>
            )}
            {showErrors &&
              !!routingNumber &&
              routingNumber.length <= MAX_ROUTING_NUMBER &&
              !isValidRoutingNumber(routingNumber) && (
                <Box mt={1} color="warning.main" fontSize={12}>
                  {validationMessages.intermediaryRoutingNumber.invalid}
                </Box>
              )}
            {/* LPC-10289: C2DS validation error */}
            <C2DSFieldErrorMessages fieldKey="intermediaryRoutingNumber" />
          </Grid>
          {/* Bank Name field */}
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.IntermediaryBankName}>
              <Input
                dense={false}
                placeholder="Bank Name"
                variant="light"
                size="large"
                value={bankName}
                onChange={(v) => onChangeBankName(sanitizeAsciiName(v, 30))}
                error={false}
              />
            </FieldTooltip>
            {showErrors && !!bankName && bankName.length > 30 && (
              <Box mt={1} color="warning.main" fontSize={12}>
                Max 30 characters
              </Box>
            )}
            {showErrors && !!bankName && bankName.length <= 30 && /[^A-Za-z0-9\s-]/.test(bankName) && (
              <Box mt={1} color="warning.main" fontSize={12}>
                {validationMessages.bankName.invalid}
              </Box>
            )}
          </Grid>
          {/* Payment Message field - shown for both SWIFT and FedWire */}
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.IntermediaryPaymentMessage}>
              <Input
                dense={false}
                placeholder="Payment Message"
                variant="light"
                size="large"
                value={paymentMessage}
                onChange={(v) => onChangePaymentMessage?.(sanitizePaymentMessage(v))}
                sanitizeOnPaste={(t) => sanitizePaymentMessage(t)}
                error={showErrors && !!paymentMessage && !isValidPaymentMessage(paymentMessage)}
              />
            </FieldTooltip>
            {showErrors && !!paymentMessage && paymentMessage.length > MAX_PAYMENT_MESSAGE && (
              <Box mt={1} color="warning.main" fontSize={12}>
                Max 40 characters
              </Box>
            )}
            {showErrors &&
              !!paymentMessage &&
              paymentMessage.length <= MAX_PAYMENT_MESSAGE &&
              !isValidPaymentMessage(paymentMessage) && (
                <Box mt={1} color="warning.main" fontSize={12}>
                  {validationMessages.paymentMessage.invalid}
                </Box>
              )}
          </Grid>
        </>
      )}
    </>
  );
};

export default memo(IntermediaryFields);
